import { useState, useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    privacy: false,
  });
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const section = sectionRef.current;
    const header = headerRef.current;
    const form = formRef.current;
    const info = infoRef.current;
    if (!section || !header || !form || !info) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 78%",
        toggleActions: "play none none none",
      },
    });

    tl.fromTo(
      header,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }
    );

    tl.fromTo(
      form,
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" },
      "-=0.5"
    );

    tl.fromTo(
      info,
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" },
      "-=0.6"
    );

    return () => {
      tl.scrollTrigger?.kill();
      tl.kill();
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === "message") {
      const clean = value.slice(0, 500);
      setCharCount(clean.length);
      setFormData((prev) => ({ ...prev, [name]: clean }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, privacy: e.target.checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.privacy) return;
    setStatus("sending");

    const body = new URLSearchParams();
    body.append("name", formData.name);
    body.append("email", formData.email);
    if (formData.phone) body.append("phone", formData.phone);
    body.append("message", formData.message);

    try {
      const res = await fetch("https://readdy.ai/api/form/d8nhmaojb57qogjbhj90", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setFormData({ name: "", email: "", phone: "", message: "", privacy: false });
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section ref={sectionRef} id="contact" className="bg-background-100 py-24 md:py-32 lg:py-36 px-6 md:px-10 lg:px-14">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className="mb-16 md:mb-20">
          <h2 className="font-heading font-bold uppercase text-[26px] sm:text-[36px] md:text-[52px] lg:text-[64px] text-foreground-950 tracking-tight leading-[0.95]">
            Kontakt
          </h2>
          <p className="mt-5 text-foreground-600 text-base md:text-lg leading-relaxed max-w-xl">
            Lassen Sie uns über Ihr Projekt sprechen — kostenlos und unverbindlich.
          </p>
        </div>

        {/* Two columns: Form + Contact Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-10 items-stretch">
          {/* Form */}
          <div ref={formRef} className="h-full">
            {status === "success" ? (
              <div className="bg-background-50 rounded-2xl p-10 md:p-12 border border-[#4CAF50]/20 h-full flex flex-col">
                <div className="flex items-center gap-4 mb-4">
                  <span className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: "#4CAF50" }}>
                    <i className="ri-check-line text-white w-6 h-6 flex items-center justify-center text-lg" />
                  </span>
                  <div>
                    <h3 className="font-heading font-bold text-foreground-950 text-lg">Vielen Dank für Ihre Nachricht!</h3>
                    <p className="text-foreground-600 text-sm mt-1">Wir melden uns innerhalb von 24 Stunden bei Ihnen.</p>
                  </div>
                </div>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-2xl p-8 md:p-10 space-y-6 h-full flex flex-col"
              >
                {/* Name */}
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Name <span className="text-foreground-400">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ihr vollständiger Name"
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-foreground-200 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-[#4CAF50] transition-colors"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    E-Mail-Adresse <span className="text-foreground-400">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="ihre@email.de"
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-foreground-200 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-[#4CAF50] transition-colors"
                  />
                </div>

                {/* Telefon */}
                <div>
                  <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Telefonnummer <span className="text-foreground-400 font-normal text-xs">(optional)</span>
                  </label>
                  <input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+49 2822 791010"
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-foreground-200 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-[#4CAF50] transition-colors"
                  />
                </div>

                {/* Nachricht */}
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Ihre Nachricht <span className="text-foreground-400">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Beschreiben Sie kurz Ihr Projekt oder Ihre Frage ..."
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-foreground-200 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-[#4CAF50] transition-colors resize-none"
                  />
                  <p className="text-foreground-400 text-xs mt-1.5 text-right">{charCount}/500 Zeichen</p>
                </div>

                {/* Datenschutz */}
                <div className="flex items-start gap-3">
                  <input
                    id="contact-privacy"
                    name="privacy"
                    type="checkbox"
                    required
                    checked={formData.privacy}
                    onChange={handleCheckbox}
                    className="mt-0.5 w-4 h-4 rounded accent-[#4CAF50] cursor-pointer"
                  />
                  <label htmlFor="contact-privacy" className="text-foreground-600 text-sm leading-relaxed cursor-pointer">
                    Ich stimme der Datenverarbeitung zu.{" "}
                    <a href="#" className="text-foreground-800 underline hover:text-[#4CAF50] transition-colors">
                      Datenschutzerklärung
                    </a>
                  </label>
                </div>

                {/* Submit */}
                <div>
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="inline-flex items-center px-8 py-3.5 rounded-full text-white font-body font-medium text-sm transition-all duration-300 whitespace-nowrap disabled:opacity-60"
                    style={{ backgroundColor: "#4CAF50" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#43A047")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#4CAF50")}
                  >
                    {status === "sending" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center mr-2" />
                        Wird gesendet ...
                      </>
                    ) : (
                      "Nachricht senden"
                    )}
                  </button>
                </div>

                {/* Error */}
                {status === "error" && (
                  <p className="text-red-600 text-sm mt-2">
                    Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder rufen Sie uns direkt an.
                  </p>
                )}
              </form>
            )}
          </div>

          {/* Contact Info Block */}
          <div ref={infoRef} className="h-full">
            <div className="grid grid-cols-1 gap-5 rounded-2xl overflow-hidden h-full">
              {/* Info links */}
              <div className="relative bg-[#0A0A0A] text-white rounded-2xl p-7 md:p-8 space-y-6">
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider font-medium mb-2">Telefon</p>
                  <a
                    href="tel:+492822791010"
                    className="text-white hover:text-[#4CAF50] transition-colors text-lg font-semibold whitespace-nowrap"
                  >
                    02822 791010
                  </a>
                </div>

                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider font-medium mb-2">E-Mail</p>
                  <a
                    href="mailto:info@elektro-zweering.de"
                    className="text-white hover:text-[#4CAF50] transition-colors text-base break-all"
                  >
                    info@elektro-zweering.de
                  </a>
                </div>

                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider font-medium mb-2">Adresse</p>
                  <p className="text-white text-base leading-relaxed">
                    An der Schleuse 10<br />
                    46446 Emmerich am Rhein
                  </p>
                </div>

                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider font-medium mb-2">Telefonzeiten</p>
                  <div className="space-y-1 text-white/80 text-sm">
                    <p>Mo – Do: 09:00 – 12:30 Uhr &amp; 14:00 – 16:30 Uhr</p>
                    <p>Fr: 09:00 – 14:00 Uhr</p>
                  </div>
                </div>
              </div>

              {/* Google Maps rechts */}
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=An+der+Schleuse+10,+46446+Emmerich+am+Rhein"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Route zu An der Schleuse 10, 46446 Emmerich am Rhein in Google Maps öffnen"
                className="group relative block rounded-2xl overflow-hidden bg-[#0A0A0A] h-[320px] md:h-[360px]"
              >
                <iframe
                  title="Standort Zweering Elektrotechnik"
                  src="https://www.google.com/maps?q=An+der+Schleuse+10,+46446+Emmerich+am+Rhein&z=14&output=embed"
                  className="absolute inset-0 w-full h-full border-0 pointer-events-none grayscale-[15%] group-hover:grayscale-0 transition-all duration-500"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                {/* Klick-Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-transparent" />
                <div className="absolute left-5 right-5 bottom-5 flex items-center justify-between gap-3">
                  <div className="text-white">
                    <p className="text-white/60 text-[11px] uppercase tracking-wider font-medium">Standort</p>
                    <p className="text-sm font-semibold">Route planen</p>
                  </div>
                  <span className="w-11 h-11 rounded-full bg-[#4CAF50] flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <i className="ri-arrow-right-up-line text-white w-5 h-5 flex items-center justify-center text-lg" />
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}